import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { personName, Project, ProjectUser, projectService } from '../../services/projectService';
import { AssigneeAvatar } from './projectTaskDisplay';

const parseDay = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const toIsoDay = (value: Date | null) => {
  if (!value) return null;
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const sameDay = (left: string | null | undefined, right: Date | null) =>
  (left ? left.slice(0, 10) : '') === (toIsoDay(right) || '');

const formatHistoryDate = (value?: string) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

interface Props {
  project: Project;
  users: ProjectUser[];
  onSaved: (project: Project) => void;
  onDeleted: () => void;
  onError: (message: string) => void;
}

const ProjectDetails: React.FC<Props> = ({ project, users, onSaved, onDeleted, onError }) => {
  const creatorId = project.createdBy?._id || '';
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [startDate, setStartDate] = useState<Date | null>(parseDay(project.startDate));
  const [endDate, setEndDate] = useState<Date | null>(parseDay(project.endDate));
  const [dateReason, setDateReason] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>(() => (project.members || []).map((member) => member._id));
  const [memberSearch, setMemberSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(project.name);
    setDescription(project.description || '');
    setStartDate(parseDay(project.startDate));
    setEndDate(parseDay(project.endDate));
    setDateReason('');
    setMemberIds((project.members || []).map((member) => member._id));
  }, [project]);

  const people = useMemo(() => {
    const byId = new Map<string, ProjectUser>();
    users.forEach((person) => byId.set(person._id, person));
    (project.members || []).forEach((person) => byId.set(person._id, person));
    if (project.createdBy?._id) byId.set(project.createdBy._id, project.createdBy);
    return Array.from(byId.values());
  }, [users, project.members, project.createdBy]);

  const roster = useMemo(() => {
    const selected = people.filter((person) => memberIds.includes(person._id) || person._id === creatorId);
    return selected.sort((a, b) => {
      if (a._id === creatorId) return -1;
      if (b._id === creatorId) return 1;
      return personName(a).localeCompare(personName(b));
    });
  }, [people, memberIds, creatorId]);

  const otherPeople = useMemo(() => people.filter((person) => person._id !== creatorId), [people, creatorId]);

  const availablePeople = useMemo(
    () => otherPeople.filter((person) => !memberIds.includes(person._id)),
    [otherPeople, memberIds]
  );

  const visiblePeople = useMemo(() => {
    const query = memberSearch.trim().toLowerCase();
    return availablePeople.filter((person) => {
      if (!query) return true;
      return `${personName(person)} ${person.email}`.toLowerCase().includes(query);
    });
  }, [availablePeople, memberSearch]);

  const history = useMemo(
    () =>
      [...(project.dateChangeHistory || [])].sort((a: any, b: any) => {
        return new Date(b.changedAt || b.newValue).getTime() - new Date(a.changedAt || a.newValue).getTime();
      }),
    [project.dateChangeHistory]
  );

  const datesChanged = !sameDay(project.startDate, startDate) || !sameDay(project.endDate, endDate);

  const toggleMember = (userId: string) => {
    if (userId === creatorId) return;
    setMemberIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  };

  const save = async () => {
    if (!name.trim()) {
      onError('Project name is required');
      return;
    }
    if (datesChanged && !dateReason.trim()) {
      onError('Reason for date change is required');
      return;
    }
    try {
      setSaving(true);
      onError('');
      const nextMembers = memberIds.includes(creatorId) ? memberIds : [creatorId, ...memberIds];
      let updated = await projectService.updateProject(project._id, {
        name: name.trim(),
        description: description.trim(),
        members: nextMembers
      } as any);
      if (datesChanged) {
        updated = await projectService.updateDates(project._id, {
          startDate: toIsoDay(startDate),
          endDate: toIsoDay(endDate),
          reason: dateReason.trim()
        });
      }
      setDateReason('');
      onSaved(updated);
    } catch (err: any) {
      onError(err.response?.data?.message || 'Could not save project details');
    } finally {
      setSaving(false);
    }
  };

  const deletePermanently = async () => {
    if (!window.confirm(`Permanently delete “${project.name}”? This cannot be undone.`)) return;
    try {
      await projectService.deleteProject(project._id);
      onDeleted();
    } catch (err: any) {
      onError(err.response?.data?.message || 'Could not delete project');
    }
  };

  return (
    <div className="project-details">
      <div className="project-details-toolbar">
        <div>
          <h2>Project details</h2>
          <p>Name, schedule, and who can see this project.</p>
        </div>
        <Button label={saving ? 'Saving…' : 'Save changes'} onClick={save} disabled={saving || !name.trim()} />
      </div>

      <div className="project-details-grid">
        <section className="project-details-card">
          <header className="project-details-card-head">
            <h3>About</h3>
          </header>
          <div className="form-group">
            <label htmlFor="project-details-name">Project name</label>
            <InputText
              id="project-details-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Project name"
            />
          </div>
          <div className="form-group">
            <label htmlFor="project-details-description">Description</label>
            <InputTextarea
              id="project-details-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              autoResize
              placeholder="What this project covers"
            />
          </div>
        </section>

        <section className="project-details-card">
          <header className="project-details-card-head">
            <h3>Schedule</h3>
          </header>
          <div className="form-row">
            <div className="form-group">
              <label>Start date</label>
              <Calendar
                value={startDate}
                onChange={(e) => setStartDate(e.value as Date | null)}
                dateFormat="dd M yy"
                placeholder="Start date"
                showButtonBar
                appendTo={document.body}
                baseZIndex={3500}
                className="w-full"
                panelClassName="projects-datepicker"
              />
            </div>
            <div className="form-group">
              <label>End date</label>
              <Calendar
                value={endDate}
                onChange={(e) => setEndDate(e.value as Date | null)}
                dateFormat="dd M yy"
                placeholder="End date"
                showButtonBar
                appendTo={document.body}
                baseZIndex={3500}
                className="w-full"
                panelClassName="projects-datepicker"
              />
            </div>
          </div>
          {datesChanged ? (
            <div className="form-group">
              <label htmlFor="project-details-reason">Reason for date change</label>
              <InputTextarea
                id="project-details-reason"
                value={dateReason}
                onChange={(e) => setDateReason(e.target.value)}
                rows={3}
                autoResize
                placeholder="Why are these dates moving?"
              />
            </div>
          ) : (
            <p className="project-details-note">A reason is required only if you change these dates.</p>
          )}
          {history.length > 0 && (
            <div className="date-history">
              <p className="date-history-label">Previous changes</p>
              {history.map((entry: any, index: number) => (
                <div key={`${entry.field}-${entry.changedAt || index}`} className="date-history-row">
                  <span>
                    <strong>{entry.field === 'startDate' ? 'Start' : 'End'}:</strong>{' '}
                    {formatHistoryDate(entry.oldValue) || '—'} → {formatHistoryDate(entry.newValue) || '—'}
                    {entry.reason ? ` · ${entry.reason}` : ''}
                  </span>
                  <em>
                    {entry.changedBy ? personName(entry.changedBy) : ''}
                    {entry.changedAt ? `, ${formatHistoryDate(entry.changedAt)}` : ''}
                  </em>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="project-details-card project-details-card-wide">
          <header className="project-details-card-head">
            <div>
              <h3>Team</h3>
              <p>
                {roster.length === 1
                  ? '1 person on this project'
                  : `${roster.length} people on this project`}
              </p>
            </div>
          </header>

          <div className="details-roster">
            {roster.map((person) => {
              const isCreator = person._id === creatorId;
              return (
                <div key={person._id} className="details-roster-row">
                  <AssigneeAvatar user={person} />
                  <span className="details-roster-copy">
                    <strong>{personName(person)}</strong>
                    <small>{person.email}</small>
                  </span>
                  {isCreator ? (
                    <span className="details-creator-badge">Creator</span>
                  ) : (
                    <button type="button" className="text-btn" onClick={() => toggleMember(person._id)}>
                      Remove
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {otherPeople.length === 0 ? (
            <p className="project-details-note">
              No other company members to add yet. Create them in Users, then they can join this project.
            </p>
          ) : availablePeople.length === 0 ? (
            <p className="project-details-note">Everyone in the company is already on this project.</p>
          ) : (
            <div className="details-add-people">
              <p className="details-add-label">Add company members</p>
              {availablePeople.length > 5 && (
                <div className="create-member-search">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
                  </svg>
                  <InputText
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Search by name or email..."
                  />
                </div>
              )}
              <div className="create-member-list">
                {visiblePeople.length === 0 && (
                  <p className="project-details-note">No company members match that search.</p>
                )}
                {visiblePeople.map((person) => (
                  <button
                    key={person._id}
                    type="button"
                    className="create-member-row"
                    onClick={() => toggleMember(person._id)}
                  >
                    <span className="create-member-check" />
                    <AssigneeAvatar user={person} />
                    <span className="create-member-copy">
                      {personName(person)}
                      <small> {person.email}</small>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {project.status === 'archived' && (
          <section className="project-details-card project-details-card-wide details-danger">
            <header className="project-details-card-head">
              <h3>Danger zone</h3>
            </header>
            <p>This project is archived. Permanently deleting it cannot be undone.</p>
            <Button label="Delete permanently" className="p-button-danger" onClick={deletePermanently} />
          </section>
        )}
      </div>
    </div>
  );
};

export default ProjectDetails;
